import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getWorkOrderById, markWorkAsCompleted } from '../../services/AppService';
import { resolveMediaUrl } from '../../utility/media';
import closeButton from "../../assets/images/close.png";
import PageFade from '../../components/Login/PageFade';
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from '../../components/AdminComponents/Modals/ConfirmActionModal';

const WorkCompletionForm = () => {
    const { workOrderId } = useParams();
    const { state: locationState } = useLocation(); 
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [workOrder, setWorkOrder] = useState(locationState || null);
    const [isHovering, setIsHovering] = useState(false);
    
    const [formData, setFormData] = useState({ completeDate: "", remark: "" });
    const [selectedFile, setSelectedFile] = useState(null);

    const [confirmModal, setConfirmModal] = useState(false);
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    // Utility: Convert backend [YYYY, M, D] array to YYYY-MM-DD string for form inputs
    const formatArrayToDate = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray)) return dateArray || "";
        const [year, month, day] = dateArray;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    };

    const loadDetails = useCallback(async () => {
        try {
            const data = await getWorkOrderById(workOrderId);
            setWorkOrder(data);
            if (data.isCompleted) {
                setFormData({
                    completeDate: formatArrayToDate(data.completeDate),
                    remark: data.remark || ""
                });
            }
        } catch (error) {
            console.error("Failed to load work order details", error);
        }
    }, [workOrderId]);

    useEffect(() => { loadDetails(); }, [loadDetails]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setConfirmModal(true);
    };

    const handleConfirm = async () => {
        setConfirmModal(false);
        const data = new FormData();
        const payload = { 
            completeDate: formData.completeDate, 
            remark: formData.remark, 
            isCompleted: true 
        };

        data.append("completionData", new Blob([JSON.stringify(payload)], { type: "application/json" }));
        if (selectedFile) data.append("file", selectedFile);

        try {
            await markWorkAsCompleted(workOrderId, data);
            setMessageModal({ 
                open: true, 
                type: "success", 
                title: t('common.success'), 
                message: "Work marked as completed successfully" 
            });
            loadDetails(); // Refresh to show in the table below
        } catch (error) {
            setMessageModal({ 
                open: true, 
                type: "error", 
                title: t('common.error'), 
                message: error.response?.data?.message || "Action Failed" 
            });
        }
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-4 md:p-6 relative">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-lg md:text-xl font-semibold text-blue uppercase">
                        {t('pageTitles.worksCompletionList') || "Add / Edit Work Completed Details"}
                    </h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                </div>

                {/* Hover Box for Reliability */}
                <div className="mb-6 relative">
                    <div className="font-bold">
                        {t('work.workName')}: 
                        <span 
                            className="text-blue-600 underline cursor-pointer ml-2" 
                            onMouseEnter={() => setIsHovering(true)} 
                            onMouseLeave={() => setIsHovering(false)}
                        >
                            {workOrder?.workTitle || "Loading..."}
                        </span>
                    </div>
                    {isHovering && workOrder && (
                        <div className="absolute z-50 top-6 left-0 bg-[#6b7ecb] text-white p-4 rounded-lg shadow-xl border w-full md:max-w-2xl">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm font-semibold">
                                <div>{t('work.workOrderNo')}: <strong>{workOrder.workOrderNo}</strong></div>
                                <div>{t('approvals.workStartDate')}: <strong>{formatArrayToDate(workOrder.workStartDate)}</strong></div>
                                <div>{t('work.orderDate')}: <strong>{formatArrayToDate(workOrder.workOrderDate)}</strong></div>
                                <div>{t('approvals.workEndDate')}: <strong>{formatArrayToDate(workOrder.workEndDate)}</strong></div>
                                <div className="sm:col-span-2">{t('approvals.workDuration')}: <strong>{workOrder.duration} Days</strong></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Form Section */}
                <form className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-8 md:gap-y-4 mb-8 bg-gray-50 p-4 md:p-6 rounded-xl border" onSubmit={handleSubmit}>
                    <div className="md:col-span-2 flex flex-col md:flex-row md:items-center text-sm gap-1 md:gap-0">
                        <label className="w-full md:w-40 font-bold text-gray-600 md:text-right">{t('common.completionDate')} (*)</label>
                        <span className="hidden md:inline px-3 font-bold text-gray-800">:</span>
                        <input 
                            type="date" 
                            className="w-full md:w-64 border-2 p-1.5 rounded-lg outline-none focus:border-blue-400 bg-white" 
                            value={formData.completeDate} 
                            onChange={e => setFormData({...formData, completeDate: e.target.value})} 
                            required 
                        />
                    </div>

                    <div className="md:col-span-2 flex flex-col md:flex-row items-start text-sm gap-1 md:gap-0">
                        <label className="w-full md:w-40 font-bold text-gray-600 md:text-right md:mt-2">{t('common.remarks')} (*)</label>
                        <span className="hidden md:inline px-3 font-bold text-gray-800 mt-2">:</span>
                        <textarea 
                            className="w-full md:flex-1 border-2 p-2 rounded-lg outline-none focus:border-blue-400 bg-white font-medium min-h-[100px]" 
                            placeholder="Remark"
                            value={formData.remark} 
                            onChange={e => setFormData({...formData, remark: e.target.value})} 
                            required 
                        />
                    </div>

                    <div className="md:col-span-2 flex flex-col md:flex-row md:items-center text-sm gap-1 md:gap-0">
                        <label className="w-full md:w-40 font-bold text-gray-600 md:text-right">{t('common.attachments')} (*)</label>
                        <span className="hidden md:inline px-3 font-bold text-gray-800">:</span>
                        <div className="flex-1 w-full">
                            <input 
                                type="file" 
                                className="w-full border rounded p-1 bg-white text-xs" 
                                onChange={e => setSelectedFile(e.target.files[0])} 
                                required={!workOrder?.isCompleted} 
                            />
                        </div>
                    </div>

                    <div className="md:col-span-2 text-center mt-4 border-b pb-4">
                        <button type="submit" className=" staff-btn-save w-full md:w-auto px-16">
                            {workOrder?.isCompleted ? t('common.update') : t('common.add')}
                        </button>
                    </div>
                </form>

                {/* Table: Added Completion Details */}
                <div className="overflow-x-auto">
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead className="bg-[#a0adf1] text-white">
                            <tr>
                                <th className="p-2 border border-black w-16 text-center">{t('tables.srNo')}</th>
                                <th className="p-2 border border-black text-center w-48">{t('common.completionDate')}</th>
                                <th className="p-2 border border-black text-left">{t('common.remarks')}</th>
                                <th className="p-2 border border-black text-center w-64">{t('common.attachments')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {workOrder?.isCompleted ? (
                                <tr className="bg-white border-b">
                                    <td className="p-2 border text-center">1</td>
                                    <td className="p-2 border text-center">{formatArrayToDate(workOrder.completeDate)}</td>
                                    <td className="p-2 border">{workOrder.remark}</td>
                                    <td className="p-2 border text-center bg-gray-50">
                                        {workOrder.fileLocation ? (
                                            <a 
                                                href={resolveMediaUrl(workOrder.fileLocation)} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-gray-700 bg-white border px-3 py-1 rounded text-xs shadow-sm inline-block"
                                            >
                                                {workOrder.fileName}
                                            </a>
                                        ) : "No Attachment"}
                                    </td>
                                </tr>
                            ) : (
                                <tr>
                                    <td colSpan="4" className="p-4 text-center text-gray-500 italic">No completion records found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmActionModal 
                open={confirmModal} 
                onClose={() => setConfirmModal(false)} 
                onConfirm={handleConfirm} 
                title="Confirm Completion" 
                message="Are you sure you want to mark this work as completed?" 
            />
            
            <MessageModal 
                open={messageModal.open} 
                onClose={() => setMessageModal({...messageModal, open: false})} 
                {...messageModal} 
            />
        </PageFade>
    );
};

export default WorkCompletionForm;